import type { Feature, FeatureCollection, LineString, Point, Polygon as GeoJsonPolygon } from 'geojson'
import type {
  GeographicCoordinates,
  Polygon,
  RouteSegment,
  SignPostPlanningData,
  StreetNodePlanningData,
  StreetSegmentPlanningData,
  TechnicalSegmentPlanningData,
} from '@/api/main/types.gen'
import { multiPolygonToGeoJsonPolygons } from '@/utils/geojson'
import type { NodeFeatureProperties } from './types'
import { VIEWPORT_BUFFER_RATIO } from './constants'

/** Ensures GeoJSON ring closure: Mapbox rejects polygons whose first/last coords differ. */
export function polygonToGeoJsonFeature(
  bounds: Polygon,
  properties: Record<string, string | number | boolean | null> = {}
): Feature<GeoJsonPolygon> {
  const ring: Array<[number, number]> = bounds.points.map(p => [p.longitude, p.latitude])
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]])
  }
  return {
    type: 'Feature',
    properties,
    geometry: { type: 'Polygon', coordinates: [ring] },
  }
}

export function planningAreasToFeatureCollection(
  areas: Array<{ id: string; bounds: Polygon; name: string | null }>
): FeatureCollection<GeoJsonPolygon> {
  return {
    type: 'FeatureCollection',
    features: areas.map(area =>
      polygonToGeoJsonFeature(area.bounds, {
        areaId: area.id,
        name: area.name ?? area.id,
      })
    ),
  }
}

export function segmentToLineCoordinates(points: GeographicCoordinates[]): Array<[number, number]> {
  return points.map(p => [p.longitude, p.latitude])
}

export function streetSegmentsToFeatureCollection(
  segments: StreetSegmentPlanningData[],
  options?: {
    getProperties?: (segment: StreetSegmentPlanningData) => Record<string, string | number | boolean | null>
  }
): FeatureCollection<LineString> {
  return {
    type: 'FeatureCollection',
    features: segments.map(segment => ({
      type: 'Feature',
      properties: {
        segmentId: segment.street_segment_id,
        ...(options?.getProperties?.(segment) ?? {}),
      },
      geometry: {
        type: 'LineString',
        coordinates: segmentToLineCoordinates(segment.geom.points),
      },
    })),
  }
}

/** LineString coords oriented entrance→exit for route travel direction. */
export function orientedStreetSegmentCoordinates(
  streetSegment: StreetSegmentPlanningData,
  entranceNodeId: string
): Array<[number, number]> {
  const travelingForward = entranceNodeId === streetSegment.source_node_id
  const points = travelingForward ? streetSegment.geom.points : [...streetSegment.geom.points].reverse()
  return segmentToLineCoordinates(points)
}

export function orientedRouteSegmentCoordinates(
  routeSegment: RouteSegment,
  streetSegment: StreetSegmentPlanningData
): Array<[number, number]> {
  return orientedStreetSegmentCoordinates(streetSegment, routeSegment.entrance_node_id)
}

export function routeSegmentsToFeatureCollection(
  routeSegments: RouteSegment[],
  indexedSegments: Record<string, StreetSegmentPlanningData>,
  properties: Record<string, string | number | boolean | null> = {}
): FeatureCollection<LineString> {
  const features: Array<Feature<LineString>> = []
  for (const routeSegment of routeSegments) {
    const segment = indexedSegments[routeSegment.street_segment_id]
    if (!segment) continue
    features.push({
      type: 'Feature',
      properties: {
        segmentId: routeSegment.street_segment_id,
        ...properties,
        isBidirectional: segment.is_bidirectional,
      },
      geometry: {
        type: 'LineString',
        coordinates: orientedRouteSegmentCoordinates(routeSegment, segment),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export type LngLatBoundsLike = {
  west: number
  south: number
  east: number
  north: number
}

/** Pads viewport bounds so edge nodes/segments stay rendered while panning slightly. */
export function expandBounds(bounds: LngLatBoundsLike, bufferRatio = VIEWPORT_BUFFER_RATIO): LngLatBoundsLike {
  const lngPad = (bounds.east - bounds.west) * bufferRatio
  const latPad = (bounds.north - bounds.south) * bufferRatio
  return {
    west: bounds.west - lngPad,
    south: bounds.south - latPad,
    east: bounds.east + lngPad,
    north: bounds.north + latPad,
  }
}

export function isPointInBounds(position: GeographicCoordinates, bounds: LngLatBoundsLike): boolean {
  return (
    position.longitude >= bounds.west &&
    position.longitude <= bounds.east &&
    position.latitude >= bounds.south &&
    position.latitude <= bounds.north
  )
}

/**
 * Viewport-culls node points; `alwaysIncludeIds` keeps path/hint/gap nodes visible even
 * when outside the current bounds (labels must remain on-map).
 */
export function nodesToFeatureCollection(
  nodes: StreetNodePlanningData[],
  getProperties: (node: StreetNodePlanningData) => NodeFeatureProperties,
  viewportBounds?: LngLatBoundsLike | null,
  alwaysIncludeIds?: Set<string>
): FeatureCollection<Point> {
  const features: Array<Feature<Point>> = []
  for (const node of nodes) {
    const alwaysInclude = alwaysIncludeIds?.has(node.street_node_id) ?? false
    if (viewportBounds && !alwaysInclude && !isPointInBounds(node.position, viewportBounds)) continue
    features.push({
      type: 'Feature',
      properties: getProperties(node),
      geometry: {
        type: 'Point',
        coordinates: [node.position.longitude, node.position.latitude],
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function boundsFromPoints(points: GeographicCoordinates[]): [[number, number], [number, number]] | null {
  if (points.length === 0) return null
  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  for (const p of points) {
    minLng = Math.min(minLng, p.longitude)
    maxLng = Math.max(maxLng, p.longitude)
    minLat = Math.min(minLat, p.latitude)
    maxLat = Math.max(maxLat, p.latitude)
  }
  if (!Number.isFinite(minLng)) return null
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export function boundsFromLineCollection(
  collection: FeatureCollection<LineString>
): [[number, number], [number, number]] | null {
  const points: GeographicCoordinates[] = []
  for (const feature of collection.features) {
    for (const coord of feature.geometry.coordinates) {
      const lng = coord[0]
      const lat = coord[1]
      if (lng === undefined || lat === undefined) continue
      points.push({ longitude: lng, latitude: lat })
    }
  }
  return boundsFromPoints(points)
}

export function routeCameraBounds(
  path: FeatureCollection<LineString>,
  fallback: [[number, number], [number, number]] | null
): [[number, number], [number, number]] | null {
  return boundsFromLineCollection(path) ?? fallback
}

export type RouteCameraFitDecision =
  | { action: 'skip' }
  | { action: 'wait' }
  | { action: 'fit'; bounds: [[number, number], [number, number]] }

export function decideRouteCameraFit(options: {
  routeFitKey: string | null
  fittedRouteKey: string | null
  pathSegments: FeatureCollection<LineString>
  pathSegmentCount: number
  fallbackBounds: [[number, number], [number, number]] | null
}): RouteCameraFitDecision {
  const { routeFitKey, fittedRouteKey, pathSegments, pathSegmentCount, fallbackBounds } = options
  if (!routeFitKey || routeFitKey === fittedRouteKey) return { action: 'skip' }
  const pathBounds = boundsFromLineCollection(pathSegments)
  if (pathBounds) return { action: 'fit', bounds: pathBounds }
  if (pathSegmentCount > 0) return { action: 'wait' }
  if (!fallbackBounds) return { action: 'wait' }
  return { action: 'fit', bounds: fallbackBounds }
}

/** Stable key for fit effect deps — ignores FeatureCollection identity churn. */
export function routeFitBoundsFingerprint(
  pathSegments: FeatureCollection<LineString>,
  pathSegmentCount: number,
  fallbackBounds: [[number, number], [number, number]] | null
): string {
  const pathBounds = boundsFromLineCollection(pathSegments)
  if (pathBounds) {
    return `path:${pathBounds[0][0].toFixed(5)},${pathBounds[0][1].toFixed(5)},${pathBounds[1][0].toFixed(5)},${pathBounds[1][1].toFixed(5)}`
  }
  if (pathSegmentCount > 0) return 'pending'
  if (!fallbackBounds) return 'none'
  return `area:${fallbackBounds[0][0].toFixed(5)},${fallbackBounds[0][1].toFixed(5)},${fallbackBounds[1][0].toFixed(5)},${fallbackBounds[1][1].toFixed(5)}`
}

export function shouldClearPlanOnInitialRouteSync(options: {
  nextKey: string | null
  loadedRouteKey: string | null
}): boolean {
  const { nextKey, loadedRouteKey } = options
  if (nextKey !== null && loadedRouteKey === nextKey) return false
  return true
}

/**
 * Tip of a short arrow in geographic degrees. Adds 180° so stored azimuth 0° points south
 * on a north-up map (planner observation-arrow convention).
 */
function azimuthTip(position: GeographicCoordinates, azimuthDegrees: number, lengthDeg: number): [number, number] {
  const radians = ((azimuthDegrees + 180) * Math.PI) / 180
  return [position.longitude + Math.sin(radians) * lengthDeg, position.latitude + Math.cos(radians) * lengthDeg]
}

export function technicalSegmentsToFeatureCollection(
  segments: TechnicalSegmentPlanningData[]
): FeatureCollection<GeoJsonPolygon> {
  const features: Array<Feature<GeoJsonPolygon>> = []
  for (const segment of segments) {
    multiPolygonToGeoJsonPolygons(segment.geom).forEach((geometry, polyIndex) => {
      features.push({
        type: 'Feature',
        properties: {
          technicalSegmentId: segment.technical_segment_id,
          polyIndex,
        },
        geometry,
      })
    })
  }
  return { type: 'FeatureCollection', features }
}

export function signPostsToFeatureCollection(
  signPosts: SignPostPlanningData[],
  viewportBounds?: LngLatBoundsLike | null
): FeatureCollection<Point> {
  const features: Array<Feature<Point>> = []
  for (const signPost of signPosts) {
    if (viewportBounds && !isPointInBounds(signPost.position, viewportBounds)) continue
    features.push({
      type: 'Feature',
      properties: { signPostId: signPost.sign_post_id },
      geometry: {
        type: 'Point',
        coordinates: [signPost.position.longitude, signPost.position.latitude],
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function signPostAzimuthsToFeatureCollection(
  signPosts: SignPostPlanningData[],
  arrowLengthDeg: number,
  viewportBounds?: LngLatBoundsLike | null
): FeatureCollection<LineString> {
  const features: Array<Feature<LineString>> = []
  for (const signPost of signPosts) {
    if (viewportBounds && !isPointInBounds(signPost.position, viewportBounds)) continue
    const origin: [number, number] = [signPost.position.longitude, signPost.position.latitude]
    signPost.observation_azimuths.forEach((azimuth, index) => {
      features.push({
        type: 'Feature',
        properties: {
          signPostId: signPost.sign_post_id,
          azimuthIndex: index,
        },
        geometry: {
          type: 'LineString',
          coordinates: [origin, azimuthTip(signPost.position, azimuth, arrowLengthDeg)],
        },
      })
    })
  }
  return { type: 'FeatureCollection', features }
}

/**
 * Renders the original edit tail; segments before `reconnectIndex` are marked `obsolete`
 * so the map can style the discarded prefix differently from the kept suffix.
 */
export function editTailToFeatureCollection(
  tail: RouteSegment[],
  reconnectIndex: number,
  indexedSegments: Record<string, StreetSegmentPlanningData>
): FeatureCollection<LineString> {
  const features: Array<Feature<LineString>> = []
  tail.forEach((routeSegment, index) => {
    const segment = indexedSegments[routeSegment.street_segment_id]
    if (!segment) return
    features.push({
      type: 'Feature',
      properties: {
        segmentId: routeSegment.street_segment_id,
        obsolete: index < reconnectIndex,
      },
      geometry: {
        type: 'LineString',
        coordinates: orientedRouteSegmentCoordinates(routeSegment, segment),
      },
    })
  })
  return { type: 'FeatureCollection', features }
}
